import { useMemo, useState, type ReactNode } from 'react';
import {
  SCREEN_BAR_HEIGHT,
  ScreenBar,
  ScreenBarEnd,
  ScreenBarRow,
  ScreenBarStart,
} from '@voll-intelligence/ui-kit/ui/screen-bar';
import {
  BusResultsPage,
  busOffersCurrency,
  DEFAULT_BUS_RESULTS_ORDER,
  EMPTY_BUS_RESULTS_FILTERS,
  type BusFilterableOffer,
  type BusLegCities,
  type BusResultsFilterValue,
  type BusResultsOrder,
} from '@voll-intelligence/intelligence-ui-kit';
import { formatDayMonth, parseDateOnly } from '@voll-intelligence/dates';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { ScreenBarClose, useScreenBarTrail } from '../../../shared/ui/screen_bar_parts.js';
import { ScreenDialog } from '@voll-intelligence/ui-kit/ui/screen-dialog';
import type { ChatUiBusOffer } from '@voll-intelligence/genui-contract';
import {
  busFiltersCopy,
  busLegName,
  busPageCopy,
  busWeekdayDay,
  type BusLeg,
} from '../../shared/bus_results_copy.js';

/** O dia da perna (`YYYY-MM-DD`), lido da partida da primeira viagem. */
function legDayOf(offers: readonly ChatUiBusOffer[]): string {
  return offers[0]?.departure.dateTime.slice(0, 10) ?? '';
}

/** A barra do topo: ✕ que sai e, no canto direito, o `trailing` do host; a
 *  fila de chips do palco estreito na segunda linha. */
function BusResultsBar({
  onChipsSlot,
  onClose,
}: Readonly<{
  onChipsSlot: (element: HTMLElement | null) => void;
  onClose: () => void;
}>): ReactNode {
  const { t } = useGenUiI18n();
  const { takeoverTrailing } = useGenUiConfig();
  const trail = useScreenBarTrail('bus', onClose);
  return (
    <ScreenBar>
      <ScreenBarStart>
        <ScreenBarClose label={t('busResults.close')} onClick={onClose} />
        {trail}
      </ScreenBarStart>
      <ScreenBarEnd>{takeoverTrailing}</ScreenBarEnd>
      <ScreenBarRow ref={onChipsSlot} />
    </ScreenBar>
  );
}

/**
 * A TELA EM DIÁLOGO "Ver as N partidas" da conversa — a MESMA página de
 * disponibilidade da bancada de ônibus (`BusResultsPage`): título com o
 * contador, cortes, ordenação e os cards, por cima da conversa inteira, na
 * casca do takeover do carro. `Selecionar` devolve a viagem ao card da
 * conversa, onde a planta abre. O que difere da bancada: a lista é a que o agente trouxe (sem re-busca na
 * conversa), então a fileira de datas fica só no dia pedido — trocar o dia é
 * pedir ao agente.
 */
export function BusResultsDialog({
  offers,
  views,
  cities,
  leg,
  roundTrip = false,
  onSelect,
  onClose,
}: Readonly<{
  offers: readonly ChatUiBusOffer[];
  /** A projeção do kit das MESMAS viagens (`toBusFilterableOffers`). */
  views: readonly BusFilterableOffer[];
  cities: BusLegCities;
  leg: BusLeg;
  /** Ida e volta: o contador da página conta as duas pernas. */
  roundTrip?: boolean;
  /** Ausente (card congelado) ⇒ nada a escolher: o host já tirou o
   *  `Selecionar` das views. */
  onSelect?: (offerId: string) => void;
  onClose: () => void;
}>): ReactNode {
  const { t, locale } = useGenUiI18n();
  const [filters, setFilters] = useState<BusResultsFilterValue>(EMPTY_BUS_RESULTS_FILTERS);
  const [order, setOrder] = useState<BusResultsOrder>(DEFAULT_BUS_RESULTS_ORDER);
  // O slot da fila de chips do palco ESTREITO, na segunda linha da barra.
  // Estado (e não ref) porque a página o recebe como prop e porta nele.
  const [chipsSlot, setChipsSlot] = useState<HTMLElement | null>(null);
  // "Hoje" para o rótulo do dia, lido uma vez ao abrir.
  const [today] = useState(() => new Date());
  const currency = busOffersCurrency(offers);
  const day = legDayOf(offers);
  const date = parseDateOnly(day);
  const title = t('busResults.title', {
    leg: busLegName(t, leg),
    place: cities.arrival,
    date: date ? busWeekdayDay(date, locale) : '',
  });
  // Na volta a ida já está escolhida.
  const chosen = leg === 'return' ? 1 : 0;
  const legs = roundTrip ? 2 : 1;
  const copy = useMemo(() => busPageCopy(t, title, chosen, legs), [t, title, chosen, legs]);
  const filtersCopy = useMemo(() => busFiltersCopy(t, currency), [t, currency]);
  return (
    <ScreenDialog label={title} onClose={onClose}>
      <BusResultsBar onChipsSlot={setChipsSlot} onClose={onClose} />
      {/* A caixa trava a altura abaixo da barra e a página a enche: a lista rola
          no próprio `<main>` (`listScroll="own"`, #4587). */}
      <div
        className="genui2 flex min-h-0 flex-1 flex-col overflow-y-auto"
        style={{ '--hui-results-page-chrome': SCREEN_BAR_HEIGHT } as React.CSSProperties}
      >
        <BusResultsPage
          listScroll="own"
          chipsContainer={chipsSlot}
          offers={views}
          currency={currency}
          copy={copy}
          filtersCopy={filtersCopy}
          filters={filters}
          order={order}
          legs={{ steps: [] }}
          dates={{
            days: date
              ? [{ date: day, label: formatDayMonth(date, locale, today), disabled: false }]
              : [],
            selected: day,
            onSelect: () => undefined,
          }}
          onQueryChange={(next) => {
            setFilters(next.filters);
            setOrder(next.order);
          }}
          onSelect={(offerId) => onSelect?.(offerId)}
        />
      </div>
    </ScreenDialog>
  );
}
